import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { execWithArgs } from '@ipfabric/utils/fp/function';
import { pick } from '@ipfabric/utils/utils/pick';
import classNames from 'classnames';
import { option } from 'fp-ts';
import { constFalse, constTrue, flow } from 'fp-ts/lib/function';
import {
  type MouseEventHandler,
  type MutableRefObject,
  cloneElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { FC, MouseEvent, ReactElement, ReactNode } from 'react';
import { PortalToRoot } from '../PortalToRoot/PortalToRoot';
import { useClickAway } from '../hooks/useClickAway';
import { useStableCallback } from '../hooks/useStableCallback';

export type TooltipPlacementSide = 'top' | 'bottom' | 'left' | 'right';

type Placement = {
  side: TooltipPlacementSide;
  left: number;
  top: number;
  maxWidth?: number;
};

type PlaceTooltipAtDirection = (
  targetRect: DOMRect,
  tooltipElement: HTMLDivElement,
) => Placement;

type TooltipSize = 'small' | 'normal';

export type TooltipProps = {
  placement?: TooltipPlacementSide;
  /**
   * Optionally condition if the tooltip should be shown
   *
   * You can use this for example to show the tooltip only if wrapped content
   * isn't fully visible.
   *
   * Event from mouseEnter is passed as you can easily get the wrapped element
   * from e.currentTarget and don't need to maintain a ref.
   *
   * @example
   * <Tooltip
   *   headline={title}
   *   content={content}
   *   shouldShow={isOverflowingContent}
   * >
   *   <p>{content}</p>
   * </Tooltip>
   */
  shouldShow?: (e: MouseEvent) => boolean;
  headline?: string;
  content?: ReactNode;
  onHideTooltip?: () => void;
  size?: TooltipSize;
  interactive?: boolean;
  highlighted?: boolean;
};

export type TooltipChildNodeProps = {
  onMouseEnter?: (e: MouseEvent) => void;
  onMouseLeave?: (e: MouseEvent) => void;
};

export const pickTooltipChildNodeProps = pick(['onMouseEnter', 'onMouseLeave']);

export type TooltipChildNode = ReactElement<TooltipChildNodeProps>;

type Props = TooltipProps & {
  /**
   * Only one child that can emit mouse enter/leave events must be provided
   *
   * WARNING: Typescript / our stack does not infer props of rendered element in JSX,
   * so presence of mouse event props is not guarded. It is here just for documentation
   * purposes or the rare chance that it will be supported in future.
   *
   * ```
   * declare const X: React.FC<{.. some props but onMouseEnter/Leave is not present}>;
   * <Tooltip><X /></Tooltip> // will not work but no error is shown because <X /> produces JSX.Element<any>
   * <Tooltip>{React.createElement(X)}</Tooltip> // properly reports error, but nobody does this
   * ```
   */
  children: TooltipChildNode;
};

export const isOverflowingContent = (e: MouseEvent): boolean =>
  // 1 is subtracted to avoid subpixel errors; it is not worthy showing a tooltip
  // for a single-pixel hidden line and on subpixel dynamic height containers
  // we have encountered clientWidth rounded down but scrollWidth rounded up.
  e.currentTarget.scrollWidth - 1 > e.currentTarget.clientWidth ||
  e.currentTarget.scrollHeight - 1 > e.currentTarget.clientHeight;

const getHorizontalCenter = (targetRect: DOMRect) =>
  targetRect.right - targetRect.width / 2;
const getVerticalCenter = (targetRect: DOMRect) =>
  targetRect.bottom - targetRect.height / 2;

const fitsToWindow =
  (tooltipElement: HTMLDivElement) =>
  (placement: Placement): boolean =>
    placement.top >= 0 &&
    window.innerHeight >= placement.top + tooltipElement.scrollHeight &&
    placement.left >= 0 &&
    window.innerWidth >= placement.left + tooltipElement.scrollWidth;

const placeTopTooltip = (
  targetRect: DOMRect,
  tooltipElement: HTMLDivElement,
): Placement => ({
  side: 'top',
  left: getHorizontalCenter(targetRect) - tooltipElement.scrollWidth / 2,
  top: targetRect.top - tooltipElement.scrollHeight,
});

const placeBottomTooltip = (
  targetRect: DOMRect,
  tooltipElement: HTMLDivElement,
): Placement => ({
  side: 'bottom',
  left: getHorizontalCenter(targetRect) - tooltipElement.scrollWidth / 2,
  top: targetRect.bottom,
});

const placeLeftTooltip = (
  targetRect: DOMRect,
  tooltipElement: HTMLDivElement,
): Placement => ({
  side: 'left',
  left: targetRect.left - tooltipElement.scrollWidth,
  top: getVerticalCenter(targetRect) - tooltipElement.scrollHeight / 2,
  maxWidth: targetRect.left,
});

const placeRightTooltip = (
  targetRect: DOMRect,
  tooltipElement: HTMLDivElement,
): Placement => ({
  side: 'right',
  left: targetRect.right,
  top: getVerticalCenter(targetRect) - tooltipElement.scrollHeight / 2,
});

const placementOrder: {
  [Key in TooltipPlacementSide]: PlaceTooltipAtDirection[];
} = {
  right: [
    placeRightTooltip,
    placeLeftTooltip,
    placeTopTooltip,
    placeBottomTooltip,
  ],
  left: [
    placeLeftTooltip,
    placeRightTooltip,
    placeTopTooltip,
    placeBottomTooltip,
  ],
  bottom: [
    placeBottomTooltip,
    placeTopTooltip,
    placeRightTooltip,
    placeLeftTooltip,
  ],
  top: [
    placeTopTooltip,
    placeBottomTooltip,
    placeRightTooltip,
    placeLeftTooltip,
  ],
};

type TooltipBubbleProps = {
  onHide: () => void;
  placement?: TooltipPlacementSide;
  showAtDomRect: DOMRect;
  headline?: string;
  content: ReactNode;
  size: TooltipSize;
  setRef?: MutableRefObject<HTMLDivElement | null>;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
  highlighted?: boolean;
  interactive?: boolean;
};

const arrowPlacementClassName: { [Key in TooltipPlacementSide]: string } = {
  top: 'bottom-0.5 left-1/2 -translate-x-1/2 rotate-180',
  bottom: 'top-0.5 left-1/2 -translate-x-1/2',
  left: 'top-1/2 right-0 -translate-y-1/2 rotate-90',
  right: 'top-1/2 left-0 -translate-y-1/2 -rotate-90',
};

const TooltipBubble: FC<TooltipBubbleProps> = (props) => {
  const [realPlacementSide, setRealPlacementSide] =
    useState<TooltipPlacementSide>(props.placement ?? 'top');
  const [style, setStyle] = useState<
    { top: string; left: string; maxWidth: string } | undefined
  >(undefined);
  const numberOfRerenders = useRef(0);

  // Close tooltip when user clicks outside its area, but keep it open when clicking on the tooltip itself.
  // In this way user can select and copy text from interactive tooltip, if desired.
  const tooltipElementRef = useClickAway<HTMLDivElement>(() => {
    if (props.onHide) {
      props.onHide();
    }
  });

  const tooltipBubbleRect = tooltipElementRef.current?.getBoundingClientRect();

  useEffect(() => {
    if (props.setRef) {
      props.setRef.current = tooltipElementRef.current;
    }
  }, [props, tooltipElementRef.current]);

  const applyPlacement = useStableCallback((placement: Placement) => {
    setRealPlacementSide(placement.side);
    setStyle({
      top: `${placement.top}px`,
      left: `${placement.left}px`,
      maxWidth: `${placement.maxWidth ? `${placement.maxWidth}px` : undefined}`,
    });
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: custom deps on purpose
  useLayoutEffect(() => {
    numberOfRerenders.current += 1;
    // Sometimes the tooltip is rerendered too many times before it finds right position, so we need to limit it.
    // This will cause the tooltip to be placed in the last fitting (but not perfect) position after 20 attempts.
    if (numberOfRerenders.current < 15) {
      const placeFunctions = placementOrder[props.placement ?? 'top'];
      const tooltipElement = tooltipElementRef.current;
      const defaultPlaceFunction = placeFunctions[0];
      if (tooltipElement === null || defaultPlaceFunction === undefined) {
        stumble('unable to obtain tooltip reference to place it properly');
        props.onHide();
        return;
      }

      // side effect - fitting result is applied immediately during the lookup
      const someFits = placeFunctions.some(
        flow(
          execWithArgs(props.showAtDomRect, tooltipElement),
          option.fromPredicate(fitsToWindow(tooltipElement)),
          option.map(applyPlacement),
          option.fold(constFalse, constTrue),
        ),
      );

      if (!someFits) {
        applyPlacement(
          defaultPlaceFunction(props.showAtDomRect, tooltipElement),
        );
      }
    }
  }, [
    props.content,
    tooltipElementRef.current,
    tooltipBubbleRect?.x,
    tooltipBubbleRect?.y,
  ]);

  return (
    <PortalToRoot>
      <div
        className={classNames(
          'fixed z-tooltip max-w-xs',
          !style && 'invisible', // prevent flashing unpositioned tooltip
          !props.interactive && 'pointer-events-none', // prevent tooltip from blocking hover on trigger element
        )}
        ref={tooltipElementRef}
        style={style}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
        role='tooltip'
      >
        <div
          className={classNames(
            'm-2 rounded-sm text-sm shadow border border-solid',
            props.highlighted
              ? 'bg-colored text-default border-colored'
              : 'bg-colored-dark text-on-colored-dark border-colored-dark',
            props.size === 'small' ? 'p-xs' : 'p-sm',
          )}
        >
          {props.headline && (
            <div className='font-semibold'>{props.headline}</div>
          )}
          <div className='font-normal'>{props.content}</div>
        </div>
        <div
          className={classNames(
            props.highlighted ? 'border-colored' : 'border-colored-dark',
            'absolute z-tooltip size-0 border-x-[8px] border-x-transparent border-t-none border-b-[8px] border-solid',
            arrowPlacementClassName[realPlacementSide],
          )}
        />
      </div>
    </PortalToRoot>
  );
};

/**
 * Tooltip wrapper component
 *
 * Tooltip must wrap an element that accepts `onMouseEnter` & `onMouseLeave` React synthetic event props.
 *
 *  When a tooltip does not fit into window placed in desired direction, alternative placement is automatically chosen.
 */
export const Tooltip: FC<Props> = (props) => {
  const { size = 'small' } = props;
  const [showAtDomRect, setShowAtDomRect] = useState<DOMRect | null>(null);

  // Implement a delay for interactive tooltips to accommodate mouse movement from the trigger element
  // to the tooltip bubble, preventing the tooltip from closing prematurely.
  const delayHide = props.interactive ? 250 : 0;

  const tooltipRef = useRef<HTMLDivElement>(null);

  const timer = useRef<number>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const showTooltip = (targetRect: DOMRect) => {
    setShowAtDomRect(targetRect);
  };

  const hideTooltip = () => {
    setShowAtDomRect(null);
    props.onHideTooltip?.();
  };

  const handleMouseEnter = (e: MouseEvent) => {
    if (!props.shouldShow || props.shouldShow(e)) {
      clearTimeout(timer.current);
      showTooltip(e.currentTarget.getBoundingClientRect());
    }
    return props.children.props.onMouseEnter?.(e);
  };

  const handleMouseLeave = (e: MouseEvent) => {
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => hideTooltip(), delayHide);

    return props.children.props.onMouseLeave?.(e);
  };

  if (!props.content) {
    return props.children;
  }

  const childrenProps = {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };

  const handleBubbleMouseEnter = (_e: MouseEvent) => {
    clearTimeout(timer.current);
  };

  const handleBubbleMouseLeave = (e: MouseEvent) => {
    handleMouseLeave(e);
  };

  return (
    <>
      {showAtDomRect && (
        <TooltipBubble
          onHide={hideTooltip}
          placement={props.placement}
          showAtDomRect={showAtDomRect}
          headline={props.headline}
          content={props.content}
          size={size}
          setRef={tooltipRef}
          onMouseEnter={props.interactive ? handleBubbleMouseEnter : undefined}
          onMouseLeave={props.interactive ? handleBubbleMouseLeave : undefined}
          highlighted={props.highlighted}
          interactive={props.interactive}
        />
      )}
      {cloneElement(props.children, childrenProps)}
    </>
  );
};
